// Kid Chores — full chore list inside KidApp.
//
// Layout: header with running star count + percent-complete bar, then a
// full grid of every chore assigned to this kid today (both done and
// not-done). Tap any tile to toggle. Done tiles get a strike + lower
// opacity; pending-approval tiles get a ⏳ overlay.

import { useEffect, useState } from "preact/hooks";
import { api, type KidChore } from "../../lib/rpc";
import type { Me } from "../../lib/use-me";

interface KidChoresProps {
  me: Me;
  notify: (msg: string) => void;
}

export function KidChoresView({ me, notify }: KidChoresProps) {
  const [chores, setChores] = useState<KidChore[]>([]);
  const [stars, setStars] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        api.choresListForKid(me.username),
        api.choresStarsThisWeek(me.username),
      ]);
      setChores(c);
      setStars(s);
    } catch (err) {
      notify((err as Error)?.message || "couldn't load chores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, [me.username]);

  const onToggle = async (c: KidChore) => {
    try {
      const r = await api.choresToggleDone(c.id);
      if (r.action === "done") {
        notify(r.pendingApproval ? "Waiting for parent OK!" : `⭐ +${r.starsAwarded || 0}`);
      } else {
        notify("Undone");
      }
      await refresh();
    } catch (err) {
      const msg = (err as Error)?.message || "couldn't update";
      // Friendlier copy than the raw error key when the kid tries to
      // un-tap a chore whose stars are already in the bank.
      if (msg.includes("stars_already_granted")) {
        notify("Already done today — ask a parent if you need this changed.");
      } else if (msg.includes("awaiting_approval")) {
        notify("Waiting for a parent to check this — ask them if you need it changed.");
      } else {
        notify(msg);
      }
    }
  };

  const doneCount = chores.filter((c) => c.doneToday).length;
  const pct = chores.length === 0 ? 0 : Math.round((doneCount / chores.length) * 100);

  return (
    <div className="kid-chores">
      <header className="kid-chores-head">
        <div className="kid-chores-title">My chores</div>
        <div className="kid-chores-stars">⭐ {stars}</div>
      </header>

      {chores.length > 0 && (
        <div className="kid-progress">
          <div className="kid-progress-bar">
            <div className="kid-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="kid-progress-label">{doneCount} / {chores.length} done today</div>
        </div>
      )}

      {loading && <div className="kid-empty">loading…</div>}

      {!loading && chores.length === 0 && (
        <div className="kid-empty kid-empty-card">
          <div className="kid-empty-emoji">🎈</div>
          <div className="kid-empty-title">No chores yet</div>
          <div className="kid-empty-sub">Ask a parent to add some.</div>
        </div>
      )}

      {!loading && chores.length > 0 && (
        <div className="kid-tile-grid">
          {chores.map((c) => (
            <KidChoreTile key={c.id} chore={c} onToggle={() => onToggle(c)} />
          ))}
        </div>
      )}
    </div>
  );
}

interface KidChoreTileProps {
  chore: KidChore;
  onToggle: () => void;
}

function KidChoreTile({ chore, onToggle }: KidChoreTileProps) {
  const pendingApproval =
    chore.doneToday && chore.requiresApproval && chore.approvedAt == null;
  const denied = !!chore.deniedAt;
  // "Done is done" — once a kid has tapped a chore done they can't un-tap
  // it, whether the stars are already credited OR the completion is still
  // pending a parent's approval. (Un-tapping a pending one used to delete
  // it, so a "done" chore showed unchecked again.) Only a denied chore is
  // tappable, to re-attempt. Server enforces the same via
  // "stars_already_granted" / "awaiting_approval" if they bypass the UI.
  const locked = chore.doneToday && !denied;
  return (
    <button
      type="button"
      className={
        "kid-tile"
        + (chore.doneToday ? " is-done" : "")
        + (pendingApproval ? " is-pending" : "")
        + (denied ? " is-denied" : "")
        + (locked ? " is-locked" : "")
      }
      onClick={locked ? undefined : onToggle}
      disabled={locked}
      aria-disabled={locked}
    >
      <span className="kid-tile-icon">
        {denied
          ? "🙈"
          : pendingApproval
            ? "⏳"
            : (chore.doneToday ? "✅" : chore.icon || "✨")}
      </span>
      <span className="kid-tile-name">{chore.name}</span>
      {denied && (
        <span className="kid-tile-denial">
          {chore.denialReason ? `Needs another look · ${chore.denialReason}` : "Needs another look"}
        </span>
      )}
      <span className="kid-tile-stars">⭐ {chore.stars}</span>
    </button>
  );
}
